import { Box, Button, MenuItem, Select, TextField, Typography, CircularProgress } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { FacturesTable } from '../components/FacturesTable';
import apiClient from '../../../services/api-client';

const FactureRecap = () => {
    // États pour les données et la pagination
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // États pour les filtres
    const [searchInput, setSearchInput] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [periode, setPeriode] = useState("all");
    const [statutFiltre, setStatutFiltre] = useState("ALL");

    const fetchFactures = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: page,
                size: 10,
            };

            if (appliedSearch) params.search = appliedSearch;
            if (periode !== 'all') params.periode = periode;
            if (statutFiltre !== 'ALL') params.statut = statutFiltre;

            const response = await apiClient.get('/api/invoices', { params });

            setFactures(response.data.content);
            setTotalPages(response.data.totalPages);

        } catch (error) {
            console.error("Erreur lors de la récupération des factures :", error);
        } finally {
            setLoading(false);
        }
    }, [page, appliedSearch, periode, statutFiltre]);

    // Effet pour charger les données à l'initialisation et au changement des filtres appliqués
    useEffect(() => {
        fetchFactures();
    }, [fetchFactures]);

    // Effet pour écouter les notifications temps réel (WebSocket/SSE)
    useEffect(() => {
        const handleNewInvoiceNotification = () => {
            // Remise à la première page pour voir la nouvelle facture en haut de liste
            setPage(0);
            fetchFactures();
        };

        window.addEventListener('onInvoiceCreated', handleNewInvoiceNotification);

        return () => {
            window.removeEventListener('onInvoiceCreated', handleNewInvoiceNotification);
        };
    }, [fetchFactures]);

    // Gestionnaires d'événements
    const handleSearchClick = () => {
        setPage(0);
        setAppliedSearch(searchInput);
    };

    const handlePeriodeChange = (e) => {
        setPage(0);
        setPeriode(e.target.value);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 2 }}>
            <Typography variant="h4" sx={{ color: "primary.main" }}>Facture Recap</Typography>
            <Typography variant="h6" sx={{ color: "text.secondary" }}>Suivez vos factures et leurs statuts de paiement</Typography>

            <Box sx={{ mt: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 250, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" sx={{ color: "primary.main" }}>Rechercher par Numéro de Facture ou Référence</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            label="Numéro de Facture ou Référence"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSearchClick}
                        >
                            Rechercher
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, minWidth: 250, display: "flex", justifyContent: 'flex-end', gap: 1 }}>
                    <Box sx={{ minWidth: 200 }}>
                        <Typography variant="h6" sx={{ color: "primary.main" }}>Filtrer par Période</Typography>
                        <Select
                            label="Période"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={periode}
                            onChange={handlePeriodeChange}
                            sx={{ mt: 1 }}
                        >
                            <MenuItem value="all">Tous</MenuItem>
                            <MenuItem value="today">Aujourd'hui</MenuItem>
                            <MenuItem value="week">Cette semaine</MenuItem>
                            <MenuItem value="month">Ce mois-ci</MenuItem>
                            <MenuItem value="year">Cette année</MenuItem>
                        </Select>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mt: 5 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <FacturesTable
                        data={factures}
                        page={page}
                        totalPages={totalPages}
                        onPageChange={(newPage) => setPage(newPage)}

                        // 💡 On passe le statut actuel et la fonction pour le changer
                        currentStatus={statutFiltre}
                        onStatusChange={(newStatus) => {
                            setPage(0); // On revient à la page 1 si on change de filtre
                            setStatutFiltre(newStatus);
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default FactureRecap;